import { deepEquals } from '@jack-henry/frontend-utils/functions';
import { unwrapReactive } from '@jack-henry/frontend-utils/lit/decorators/deep-reactive';
import { exists, symmetricDifference } from '@treasury/utils';
import { PropertyChangeManager } from '@treasury/utils/lit-helpers';
import type {
    MultiSelectComboBoxSelectedItemsChangedEvent,
    MultiSelectComboBox as VaadinMultiSelect,
} from '@vaadin/multi-select-combo-box';
import { css, html, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import { SelectItemVm, type SelectItemVmMapper } from './forms';
import { TmInputFieldBase } from './tm-input-field.base';

// eslint-disable-next-line no-duplicate-imports
import '@vaadin/multi-select-combo-box';

const SELECT_ALL_VALUE = Symbol('MULTI_SELECT_ALL');

export const tagName = 'tm-multi-select';
@customElement(tagName)
export class TmMultiSelect<T> extends TmInputFieldBase<T[]> {
    @property({ type: Array })
    public items: T[] = [];

    @property({ type: String })
    public placeholder = 'Type to search';

    @property({ type: Boolean })
    public allowSelectAll = false;

    @property({ type: String })
    public allSelectedText = 'All Selected';

    @property({ attribute: false })
    public noItemsText = 'Nothing available to select';

    /**
     * A function that maps a value of type `T` to a `SelectItemVm<T>`.
     */
    @property({ attribute: false })
    public mapItem?: SelectItemVmMapper<T>;

    private readonly pcm = new PropertyChangeManager<TmMultiSelect<T>>(this)
        .onPropertyChange('items', current => {
            this.itemVms = this.createItemVms(current);
        })
        .onPropertyChange('initial', current => {
            this.selectedItems = this.findVms(current || []);
        });

    /**
     * The original `items` augmented with view model properties
     * needed by the Vaadin multi-select component.
     *
     * This interface is for internal use only and need not
     * be exposed to consumers of the component.
     */
    private itemVms: SelectItemVm<T | typeof SELECT_ALL_VALUE>[] = [];

    /**
     * Exists only as a backing field for `selectedItems` property.
     */
    private _selectedItems: SelectItemVm<T | typeof SELECT_ALL_VALUE>[] = [];

    private get selectedItems() {
        return this._selectedItems;
    }

    private set selectedItems(selected: SelectItemVm<T | typeof SELECT_ALL_VALUE>[]) {
        if (!Array.isArray(selected)) {
            return;
        }
        // normalize array to remove the select all item,
        // since it must be the last element and will be added below
        selected = removeSelectAll(selected);
        const prevLength = this._selectedItems.length;
        if (
            this.allowSelectAll &&
            selected.length > 0 &&
            selected.length === this.itemVms.length - 1
        ) {
            this._selectedItems = [...selected, this.selectAllItem];
        } else {
            this._selectedItems = selected;
        }

        if (includesSelectAll(this._selectedItems)) {
            this.setAttribute('allSelected', '');
        } else {
            this.removeAttribute('allSelected');
        }

        if (this.multiSelect && this._selectedItems.length !== prevLength) {
            this.multiSelect.selectedItems = this._selectedItems;
        }
    }

    private readonly inputRef: Ref<VaadinMultiSelect> = createRef();

    private selectAllItem: SelectItemVm<typeof SELECT_ALL_VALUE> = {
        label: this.allSelectedText,
        value: SELECT_ALL_VALUE,
        id: 0,
    };

    private get hasNoItems() {
        return this.itemVms.length === 0;
    }

    private get multiSelect() {
        return this.inputRef.value;
    }

    public firstUpdated(changedProps: PropertyValues) {
        super.firstUpdated(changedProps);
        this.assignDefaultMapper();
        this.pcm.handleChanges(changedProps);
    }

    public async clearValue() {
        super.clearValue();
        this.selectedItems = [];
    }

    public async changeValue(value: T[]) {
        super.changeValue(value);
        this.selectedItems = this.findVms(value);
    }

    protected updated(changedProps: PropertyValues<this>) {
        super.updated(changedProps);
        this.pcm.handleChanges(changedProps);

        if (changedProps.has('allSelectedText')) {
            this.selectAllItem.label = this.allSelectedText;
        }
    }

    protected render() {
        const finalHelperText = this.hasNoItems ? this.noItemsText : this.helperText;

        return html`
            <style>
                :host([allSelected])
                    vaadin-multi-select-combo-box
                    [slot='chip']:not([title='${this.allSelectedText}']),
                :host([allSelected]) vaadin-multi-select-combo-box [slot='overflow'] {
                    display: none;
                }

                :host([allSelected])
                    vaadin-multi-select-combo-box
                    [slot='chip'][title='${this.allSelectedText}'] {
                    color: var(--brand-primary);
                }
            </style>
            <vaadin-multi-select-combo-box
                ${ref(this.inputRef)}
                @selected-items-changed=${(
                    e: MultiSelectComboBoxSelectedItemsChangedEvent<SelectItemVm<T>>
                ) => {
                    this.onSelectItemsChanged(e);
                }}
                class="w-full"
                part="component"
                .label=${this.label}
                .items=${this.itemVms}
                .selectedItems=${this.selectedItems}
                item-id-path="id"
                item-label-path="label"
                placeholder=${this.placeholder}
                helper-text=${ifDefined(finalHelperText)}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled || this.hasNoItems}
                ?has-placeholder=${!!this.placeholder}
            >
            </vaadin-multi-select-combo-box>
        `;
    }

    protected validate() {
        if (!this.multiSelect) {
            return false;
        }

        return this.multiSelect.checkValidity();
    }

    private createItemVms(values: T[]) {
        values = unwrapReactive(values).map(unwrapReactive);
        const vms = this.mapItems(values);
        const oldVal = this.itemVms;
        const itemVms: SelectItemVm<T | typeof SELECT_ALL_VALUE>[] = vms.map((item, i) => {
            if (typeof item === 'object' && !exists(item.id)) {
                item.id = i + 1;
            }
            return item;
        });

        if (this.allowSelectAll && itemVms.length > 0) {
            itemVms.unshift(this.selectAllItem);
        }

        if (!deepEquals(itemVms, oldVal)) {
            this.requestUpdate('items', oldVal);
        }

        return itemVms;
    }

    private onSelectItemsChanged(e: MultiSelectComboBoxSelectedItemsChangedEvent<SelectItemVm<T>>) {
        e.stopPropagation();
        const items = e.detail.value;
        if (deepEquals(this.selectedItems, items)) {
            return;
        }

        /**
         * `true` if any items were added; `false` if they were removed
         */
        const itemsAdded = items.length > this.selectedItems.length;
        const diff = symmetricDifference(items, this.selectedItems);
        const itemIsSelectAll = diff.length === 1 ? isSelectAllItem(diff[0]) : false;

        // account for the select all item
        if (itemIsSelectAll && this.allowSelectAll) {
            this.selectedItems = itemsAdded ? (this.itemVms as SelectItemVm<T>[]) : [];
        } else {
            this.selectedItems = items;
        }

        const unwrapped = removeSelectAll(this.selectedItems).map(item => item.value);
        // don't call overridden version since it maps unwrapped items to VMs and sets this.selectedItems again
        super.changeValue(unwrapped);
    }

    private mapItems(values: T[]): SelectItemVm<T>[] {
        if (!this.mapItem) {
            throw new Error('No item mapper provided to <tm-multi-select>');
        }

        const { mapItem } = this;
        return values.map(v => {
            // use prior VM if it exists to preserve references for diff comparisons
            const existingVm = this.itemVms.find(vm => vm.value === v);
            if (existingVm) {
                return existingVm as SelectItemVm<T>;
            }

            return mapItem(v);
        });
    }

    /**
     * If no mapper was provided but the items array contains
     * serializable values, use the value as the label.
     */
    private assignDefaultMapper() {
        const isSerializable = (item: T) => typeof item === 'string' || typeof item === 'number';
        if (this.mapItem || this.items.some(i => !isSerializable(i))) {
            return;
        }

        this.mapItem = mapSerializable as unknown as SelectItemVmMapper<T>;
    }

    /**
     * A given a list of items of type `T`,
     * return their corresponding SelectItemVm<T> representations.
     *
     * Useful for getting the correct object references for setting selected items.
     */
    private findVms(items: T[] = []): SelectItemVm<T>[] {
        if (items.length === 0) {
            return [];
        }

        const map = removeSelectAll(this.itemVms).reduce((map, itemVm) => {
            map.set(itemVm.value, itemVm);
            return map;
        }, new Map<T, SelectItemVm<T>>());

        return items.map(item => {
            const vm = map.get(item);
            if (!vm) {
                throw new Error('Item not found in multi-select items');
            }
            return vm;
        });
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                vaadin-multi-select-combo-box [slot='overflow']::before,
                vaadin-multi-select-combo-box [slot='overflow']::after {
                    border-left: calc(var(--lumo-space-s) / 4) solid;
                    border-color: var(--border-color);
                }

                vaadin-multi-select-combo-box-chip:last-of-type {
                    margin-right: 10px;
                }

                vaadin-multi-select-combo-box-chip::part(label) {
                    position: inherit;
                    top: inherit;
                    left: inherit;
                }

                [focused]::part(label),
                [has-value]::part(label),
                [has-placeholder]::part(label) {
                    padding-bottom: 2px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmMultiSelect<unknown>;
    }
}

function removeSelectAll<T>(items: SelectItemVm<T | typeof SELECT_ALL_VALUE>[]) {
    return items.filter(item => !isSelectAllItem(item)) as SelectItemVm<T>[];
}

function includesSelectAll<T>(items: SelectItemVm<T>[]) {
    return items.some(isSelectAllItem);
}

function isSelectAllItem<T>(item: SelectItemVm<T>) {
    return item.value === SELECT_ALL_VALUE;
}

/**
 * Crete a view model from a value that is serializable, using the string representation as the label.
 */
function mapSerializable<T extends string | number>(value: T): SelectItemVm<T> {
    return {
        label: String(value),
        value,
    };
}
